import Papa from 'papaparse'
import { CSV_SEPARATOR, csvQuote, exportNumber, exportPercent } from './DataTableExportUtil.js'
import { relativeAbundance, type OtuTableRow } from './OtuCubeTable.logic.js'

/*
 * The OTU table's CSV, either mode, written in the browser from the table's rows: the columns in `EXPORT_COLUMNS`
 * order, field names as the header, text always quoted, numbers bare and ungrouped, nulls empty. The read columns
 * follow, under a totals line.
 */

type Cell = string | number | null | undefined

type ExportColumn = { field: string; text: boolean; value: (row: OtuTableRow) => Cell }

const text = (field: string, value: (row: OtuTableRow) => Cell): ExportColumn => ({ field, text: true, value })
const number = (field: keyof OtuTableRow): ExportColumn => ({ field, text: false, value: (row) => row[field] as Cell })
const textField = (field: keyof OtuTableRow): ExportColumn => text(field, (row) => row[field] as Cell)

// `fastaSequence` is left out: the table carries no sequences.
const EXPORT_COLUMNS: ExportColumn[] = [
  textField('otuUuid'),
  textField('otuId'),
  number('clusterSize'),
  number('memberCount'),
  number('minClusterSize'),
  number('avgClusterSize'),
  number('maxClusterSize'),
  text('processId', (row) => row.processId?.join(';')),
  number('minHitPercentage'),
  number('hitPercentage'),
  number('maxHitPercentage'),
  number('minGradePercentage'),
  number('gradePercentage'),
  number('maxGradePercentage'),
  textField('domain'),
  textField('kingdom'),
  textField('phylum'),
  textField('className'),
  textField('order'),
  textField('family'),
  textField('genus'),
  textField('species'),
  number('totalRawReads'),
  text('binSharing', (row) => row.binSharing?.map((b) => (b === true ? 'Y' : 'N')).join(';')),
  text('bins', (row) => row.bins?.map(String).join(';')),
  text('binSpecies', (row) => row.binSpecies?.map(String).join(';')),
  number('representation'),
  textField('evidenceEvaluatedAt')
]

/** The read columns: their labels, where each sits in a row's `sampleReads`, and its reads over the whole scope. */
export type CubeCsvReads = { labels: string[]; indices: number[]; totals: ArrayLike<number>; relative: boolean }

export type CubeCsvInput = {
  rows: OtuTableRow[]
  /** The table fields to write; `EXPORT_COLUMNS` decides their order. */
  columns: ReadonlySet<string>
  reads?: CubeCsvReads | null
  /** Written as a `# …` line above the header. */
  comment?: string | null
  formatNumber?: (value: number) => string
  formatPercent?: (value: number) => string
}

/** The fields of [columns] the export writes, in export order. */
export const cubeCsvFields = (columns: ReadonlySet<string>): string[] =>
  EXPORT_COLUMNS.filter((c) => columns.has(c.field)).map((c) => c.field)

/** Cells of a CSV with [rows] data rows of [columns] cells each, which is what makes it slow to build. */
export const csvCellCount = (rows: number, columns: number): number => rows * columns

/** Above this many cells an export asks before it is generated. */
export const EXPORT_CELL_WARNING = 200_000

export function cubeCsv({
  rows,
  columns,
  reads = null,
  comment = null,
  formatNumber = exportNumber,
  formatPercent = exportPercent
}: CubeCsvInput): string {
  const base = EXPORT_COLUMNS.filter((c) => columns.has(c.field))
  const readLabels = reads?.labels ?? []
  const unparse = (data: Cell[][], quotes: boolean[]) =>
    Papa.unparse(data as unknown[][], { delimiter: CSV_SEPARATOR, newline: '\n', quotes, header: false })
  const number = (value: Cell) => (typeof value === 'number' && !Number.isNaN(value) ? formatNumber(value) : null)

  const withReads = !!reads && readLabels.length > 0
  // The totals line's label needs a cell left of the read columns; without table columns it gets one of its own.
  const labelColumn = withReads && base.length === 0
  const lines: string[] = []
  if (comment) lines.push(`# ${comment.replace(/\s*\n\s*/g, ' ')}`)
  lines.push([...(labelColumn ? [''] : []), ...base.map((c) => c.field), ...readLabels.map(csvQuote)].join(CSV_SEPARATOR))
  if (reads && withReads) {
    // The totals stay counts, the denominators of the percents below them.
    const label = csvQuote(reads.relative ? 'Total reads per sample (absolute, not %)' : 'Total reads per sample')
    const padding = base.slice(1).map(() => '')
    lines.push([label, ...padding, ...reads.indices.map((_, k) => formatNumber(reads.totals[k]!))].join(CSV_SEPARATOR))
  }

  const zero = formatNumber(0)
  const readCell = (row: OtuTableRow, k: number): string | null => {
    const v = row.sampleReads?.[reads!.indices[k]!]
    if (v === undefined || Number.isNaN(v)) return null
    if (!reads!.relative) return v === 0 ? zero : formatNumber(v)
    const relative = relativeAbundance(v, reads!.totals[k]!)
    return Number.isNaN(relative) ? null : formatPercent(relative)
  }
  const data = rows.map((row) => [
    ...(labelColumn ? [null] : []),
    ...base.map((c) => (c.text ? (c.value(row) ?? null) : number(c.value(row)))),
    ...readLabels.map((_, k) => readCell(row, k))
  ])
  const quotes = [...(labelColumn ? [false] : []), ...base.map((c) => c.text), ...readLabels.map(() => false)]
  if (data.length > 0) lines.push(unparse(data, quotes))
  return `${lines.join('\n')}\n`
}
